/**
 * /inner-circle/success — post-Stripe-checkout landing page.
 *
 * Stripe redirects here with `?session_id=cs_test_…` after a successful
 * checkout. We POST that session_id to /api/payments/inner-circle/verify
 * which:
 *   1) Confirms the session belongs to the logged-in user
 *   2) Confirms the session is paid or in trial state
 *   3) Promotes the user to subscription_tier="founding"
 *   4) Sends the existing tpl_founder_welcome Resend email
 *
 * UX contract:
 *   - Loading shimmer for ~1-2 seconds while we hit /verify
 *   - Emerald-green success card with crest + welcome copy on success
 *   - Clear next-step CTAs: connect hardware, generate API token, claim badge
 *   - Idempotent — refreshing the page just re-verifies the same session
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Crown, ArrowRight, Plug, Drop, House, X } from '@phosphor-icons/react';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';

const InnerCircleSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ status: 'verifying', error: null, data: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'error', error: 'No session ID in URL.', data: null });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(
          `${API_URL}/api/payments/inner-circle/verify`,
          { session_id: sessionId },
          { withCredentials: true },
        );
        if (!cancelled) setState({ status: 'success', error: null, data: res.data });
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.data?.detail || 'Verification failed. Please contact support@turfpulse.app with your session ID.';
          setState({ status: 'error', error: msg, data: null });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-16" data-testid="inner-circle-success-page">
      <SEO
        title="Welcome to the Inner Circle · TurfPulse"
        description="You're in. Welcome to the TurfPulse Inner Circle."
        path="/inner-circle/success"
        noindex
      />
      <div className="max-w-2xl w-full">
        {state.status === 'verifying' && (
          <Card className="border-emerald-500/30">
            <CardContent className="p-10 text-center space-y-3" data-testid="inner-circle-verifying">
              <div className="animate-pulse">
                <Crown size={48} weight="duotone" className="mx-auto text-emerald-500" />
              </div>
              <h1 className="text-2xl font-bold">Confirming your seat…</h1>
              <p className="text-sm text-muted-foreground">Stripe accepted your card. Now flipping your account to founding-member status.</p>
            </CardContent>
          </Card>
        )}

        {state.status === 'error' && (
          <Card className="border-rose-500/40 bg-rose-500/5">
            <CardContent className="p-8 space-y-4" data-testid="inner-circle-error">
              <div className="flex items-center gap-2 text-rose-700 dark:text-rose-300">
                <X size={24} weight="bold" />
                <h1 className="text-xl font-bold">We couldn't verify your checkout</h1>
              </div>
              <p className="text-sm text-muted-foreground">{state.error}</p>
              <p className="text-xs text-muted-foreground">
                Session: <code className="font-mono">{sessionId}</code>
              </p>
              <Button asChild variant="outline" data-testid="inner-circle-error-back">
                <Link to="/integrations/home-assistant">← Back to integration page</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {state.status === 'success' && (
          <Card className="border-emerald-500/50 bg-emerald-500/5" data-testid="inner-circle-success">
            <CardContent className="p-8 sm:p-10 space-y-6">
              {/* Hero */}
              <div className="text-center space-y-3">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/15">
                  <Crown size={36} weight="duotone" className="text-emerald-600" />
                </div>
                <div className="flex items-center justify-center gap-2 text-emerald-700 dark:text-emerald-300 font-semibold text-sm tracking-wider uppercase">
                  <CheckCircle size={16} weight="fill" />
                  Welcome to the Inner Circle
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold text-foreground">You're in.</h1>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Card on file. <strong>$0 charged today.</strong> First charge of $4.99 hits Nov 1, 2026 — locked at that rate forever.
                </p>
              </div>

              {/* Confirmation strip */}
              <div className="rounded-lg border border-emerald-500/30 bg-background p-4 grid sm:grid-cols-3 gap-3 text-center text-xs">
                <div data-testid="confirm-tier">
                  <div className="text-muted-foreground uppercase tracking-wider mb-1">Tier</div>
                  <div className="font-semibold text-base text-foreground">Founding Member</div>
                </div>
                <div data-testid="confirm-trial">
                  <div className="text-muted-foreground uppercase tracking-wider mb-1">Free until</div>
                  <div className="font-semibold text-base text-foreground">Oct 31, 2026</div>
                </div>
                <div data-testid="confirm-rate">
                  <div className="text-muted-foreground uppercase tracking-wider mb-1">Then</div>
                  <div className="font-semibold text-base text-foreground">$4.99/mo · for life</div>
                </div>
              </div>

              {/* Next steps */}
              <div className="space-y-3">
                <h2 className="font-semibold text-foreground">Three things to do right now</h2>
                <Link to="/profile" className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-emerald-500/50 hover:bg-emerald-500/5 transition" data-testid="next-step-hardware">
                  <Drop size={22} weight="duotone" className="text-emerald-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">1. Connect Ecowitt + Rachio</div>
                    <div className="text-xs text-muted-foreground">Profile → Ecowitt + Rachio sections</div>
                  </div>
                  <ArrowRight size={18} className="text-muted-foreground" />
                </Link>
                <Link to="/profile" className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-emerald-500/50 hover:bg-emerald-500/5 transition" data-testid="next-step-token">
                  <Plug size={22} weight="duotone" className="text-emerald-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">2. Generate your Home Assistant API token</div>
                    <div className="text-xs text-muted-foreground">Profile → Personal API Tokens</div>
                  </div>
                  <ArrowRight size={18} className="text-muted-foreground" />
                </Link>
                <Link to="/app" className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-emerald-500/50 hover:bg-emerald-500/5 transition" data-testid="next-step-dashboard">
                  <House size={22} weight="duotone" className="text-emerald-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">3. Open your dashboard</div>
                    <div className="text-xs text-muted-foreground">See today's call live</div>
                  </div>
                  <ArrowRight size={18} className="text-muted-foreground" />
                </Link>
              </div>

              <p className="text-xs text-muted-foreground italic text-center pt-3 border-t border-border/60">
                A welcome email with the kit Amazon links + 5-step onboarding is on its way to{' '}
                <strong>{state.data?.email}</strong>.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default InnerCircleSuccessPage;
